import { type HttpClient, robotoHeaders } from "@/shared/http";
import { bigintReplacer } from "@/shared/json";

import {
  parseReadPlanTimesAsBigInt,
  READ_PLAN_VERSION,
  type ReadPlan,
  type ReadPlanRequest,
} from "./readPlan";

/**
 * Fetch the read plan for one topic from
 * `POST v2/topics/id/{topic_id}/read-plan`. `topicId` names a topic
 * identity (one topic name within an org, across all its files), not a
 * per-file topic record. The plan names backing files by `fs_node_id` and
 * carries no topic data.
 *
 * Rejects with:
 * - `RobotoInvalidRequestException` when the request is malformed or does
 *   not fit the in-window data: it names a field, schema, or timeline source
 *   that data does not use; it names no schema where the partitions carry
 *   several, or no timeline source where the schema has several and none
 *   flagged as default; its `fields_exclude` leaves no field to project; a
 *   `prefer` transformation names a kind the service does not recognize; or
 *   a `prefer` selector with a criterion set matches no stored variant of a
 *   projected field in some partition the plan reads.
 * - `RobotoNotFoundException` when the topic identity, the request's
 *   session, file, dataset, `schema_id`, or `timeline_source_id` does not
 *   exist, or when the request names no timeline source and the schema has
 *   none.
 * - `RobotoUnauthorizedException` when the caller cannot view topics in the
 *   topic's org, sessions in the session's org, the request's file or
 *   dataset, or one of the files backing in-window topic data.
 * - `Error` when the plan's `plan_version` is not `READ_PLAN_VERSION`.
 */
export async function resolveReadPlan(
  httpClient: HttpClient,
  topicId: string,
  request: ReadPlanRequest,
  options?: Partial<{
    abortSignal: AbortSignal;
    resourceOwnerId: string;
  }>,
): Promise<ReadPlan> {
  const url = httpClient.constructUrl(`v2/topics/id/${topicId}/read-plan`);
  const response = await httpClient.post(url, {
    // bigintReplacer writes every digit of the window bounds, which are
    // absolute Unix-epoch nanoseconds.
    body: JSON.stringify(request, bigintReplacer),
    signal: options?.abortSignal,
    headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
  });
  const plan = await response.json<ReadPlan>(parseReadPlanTimesAsBigInt);
  if (plan.plan_version !== READ_PLAN_VERSION) {
    throw new Error(
      `Unrecognized read plan version ${plan.plan_version}; resolveReadPlan accepts only version ${READ_PLAN_VERSION}`,
    );
  }
  return plan;
}
